import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function SentimentAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Social Media - Sentiment API" description="Query social media data via the Sentiment API; request parameters, code examples and the response schema for the social media endpoint."  canonicalUrl="https://axionquant.com/docs/02-alternative-data/sentiment/social-media" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Social Media - Sentiment API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get Social Media Sentiment */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /sentiment/:ticker/social
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Analyzes sentiment from social media platforms including Google search trends, Twitter/X conversations, and Reddit discussions. Provides real-time insights into public perception and trending topics related to the company.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;TSLA&quot;, &quot;MSFT&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/sentiment/AAPL/social', '/sentiment/TSLA/social']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">label <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Overall sentiment label (POSITIVE/NEGATIVE)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">score <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Confidence score (0.0-1.0)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">breakdown</p>
                  <div className="ml-2 mt-1 space-y-2">
                    <p className="text-xs text-gray-500">Detailed breakdown of positive and negative segments</p>
                    <div className="border-l-4 border-gray-300 pl-3">
                      <p className="font-mono text-xs text-gray-800">positive</p>
                      <p className="text-xs text-gray-600">Contains count and avgScore for positive sentiment</p>
                      <p className="font-mono text-xs text-gray-800 mt-1">negative</p>
                      <p className="text-xs text-gray-600">Contains count and avgScore for negative sentiment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Social Media Sentiment" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Social Media Sentiment</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="sentiment/social"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="02-alternative-data/sentiment/social-media"></Code>
              </div>
            </div>
          </div>
        </div>



      </div>
    </div>
  );
}
